import { translateLegacyText } from "./legacyTranslation";

// The yield service sends presentation labels in English, including labels
// assembled from model names. Translate before rendering SVG axes/tooltips.
const LABELS = {
  "Global horizontal irradiation": "Ολική οριζόντια ακτινοβολία",
  "Tilt and orientation (transposition)": "Κλίση και προσανατολισμός (μετατροπή στο επίπεδο των πλαισίων)",
  "Far shading (terrain horizon)": "Μακρινή σκίαση (ορίζοντας εδάφους)",
  "Rear-side gain: G_rear,eff = φ × rear irradiation": "Όφελος πίσω όψης: G_rear,eff = φ × ακτινοβολία πίσω όψης",
  "Rear-side gain (bifacial)": "Όφελος πίσω όψης (πλαίσια διπλής όψης)",
  "Optics: incidence angle, spectrum, row shading": "Οπτικές επιδράσεις: γωνία πρόσπτωσης, φάσμα και σκίαση σειρών",
  "Optics: incidence angle, spectrum, near shading": "Οπτικές επιδράσεις: γωνία πρόσπτωσης, φάσμα και κοντινή σκίαση",
  "Optics: IAM, soiling, spectral, row shading": "Οπτικές επιδράσεις: γωνία πρόσπτωσης, ρύπανση, φάσμα και σκίαση σειρών",
  "Module conversion at STC efficiency": "Μετατροπή πλαισίων με απόδοση σε πρότυπες συνθήκες (STC)",
  "Cell temperature and low-light response": "Θερμοκρασία κυψελών και απόκριση σε χαμηλή ακτινοβολία",
  "DC losses: soiling, mismatch, wiring, LID, connections": "Απώλειες DC: ρύπανση, ανομοιομορφία, καλωδίωση, LID και συνδέσεις",
  "MPP tracking window": "Εύρος τάσης παρακολούθησης μέγιστης ισχύος (MPPT)",
  "Inverter conversion, clipping and standby": "Μετατροπή αντιστροφέα, περιορισμός ισχύος και αναμονή",
  "AC wiring to the main board": "Καλωδίωση AC έως τον κεντρικό πίνακα",
  "Step-up transformer (no-load and load)": "Μετασχηματιστής ανύψωσης (απώλειες κενού και φορτίου)",
  "Incidence angle on diffuse and albedo": "Γωνία πρόσπτωσης στη διάχυτη και ανακλώμενη ακτινοβολία",
  "Row shading, geometric": "Σκίαση σειρών — γεωμετρική",
  "Row shading, electrical (bypass diodes)": "Σκίαση σειρών — ηλεκτρική (δίοδοι παράκαμψης)",
  "Near shading, geometric": "Κοντινή σκίαση — γεωμετρική",
  "Near shading, electrical (bypass diodes)": "Κοντινή σκίαση — ηλεκτρική (δίοδοι παράκαμψης)",
  "Near shading, diffuse masking": "Κοντινή σκίαση — επικάλυψη διάχυτης ακτινοβολίας",
  "Sky model substitution (row field)": "Αντικατάσταση μοντέλου ουρανού (πεδίο σειρών)",
  "Near shading, electrical mismatch (MPPT I-V)": "Κοντινή σκίαση — ηλεκτρική αναντιστοιχία (MPPT I-V)",
  "Other module conversion effects": "Λοιπές επιδράσεις μετατροπής πλαισίων",
  "Spectral correction": "Φασματική διόρθωση",
  "Diffuse utilization factor": "Συντελεστής αξιοποίησης διάχυτης ακτινοβολίας",
  "SAPM optical response": "Οπτική απόκριση SAPM",
  "Cell temperature above 25 °C": "Θερμοκρασία κυψελών πάνω από 25 °C",
  "Low light, module quality and the remainder": "Χαμηλός φωτισμός, ποιότητα πλαισίων και το υπόλοιπο",
  "Inverter loss during operation (efficiency)": "Απώλεια αντιστροφέα κατά τη λειτουργία (απόδοση)",
  "Inverter loss over nominal AC power": "Απώλεια αντιστροφέα πάνω από την ονομαστική ισχύ AC",
  "Inverter loss due to power threshold": "Απώλεια αντιστροφέα λόγω κατωφλίου ισχύος",
  "Inverter standby (night)": "Αναμονή αντιστροφέα (νύχτα)",
  "Conversion efficiency and clipping": "Απόδοση μετατροπής και περιορισμός ισχύος",
  "Transformer no-load (iron loss)": "Απώλειες κενού μετασχηματιστή (σιδήρου)",
  "Load loss": "Απώλειες φορτίου",
};

const SHORT = {
  "Module conversion at STC efficiency": ["Μετατροπή πλαισίων (STC)", "Module conversion (STC)"],
  "Cell temperature and low-light response": ["Θερμοκρασία και χαμηλό φως", "Temperature and low light"],
  "DC losses: soiling, mismatch, wiring, LID, connections": ["Απώλειες DC", "DC losses"],
  "MPP tracking window": ["Εύρος τάσης MPPT", "MPPT voltage window"],
  "Inverter conversion, clipping and standby": ["Μετατροπή και απώλειες αντιστροφέα", "Inverter conversion and losses"],
  "AC wiring to the main board": ["Καλωδίωση AC", "AC wiring"],
  "Step-up transformer (no-load and load)": ["Μετασχηματιστής ανύψωσης", "Step-up transformer"],
};

export function yieldLabel(value, isGreek) {
  if (!isGreek || !value) return value;
  if (LABELS[value]) return LABELS[value];
  const iam = /^Incidence angle \((.+)\)$/.exec(value);
  if (iam) return `Γωνία πρόσπτωσης (${iam[1]})`;
  return translateLegacyText(value);
}

export function shortYieldLabel(value, isGreek) {
  return SHORT[value]?.[isGreek ? 0 : 1] || yieldLabel(value, isGreek);
}
